/** O nome curto de um modelo, para dizer de que modelo vem cada elemento.
 *
 *  Com vários modelos federados (ARQ, EST, MAC, HID, EEL), um elemento
 *  sem o modelo de origem não se identifica. O nome curto parte do mesmo
 *  nome que a lista de modelos mostra (o ficheiro, ou o id quando dois
 *  modelos vêm do mesmo ficheiro):
 *
 *  - com uma sigla de especialidade como segmento do nome (separada por
 *    hífen ou sublinhado): "MAC · Cha139-…-MAC-AP-0001";
 *  - sem sigla: o nome sem extensão, truncado ao meio.
 *
 *  Onde há pouco espaço usa-se só a sigla (ou o nome truncado), com o
 *  nome completo do ficheiro no tooltip. */

/** Siglas de especialidade reconhecidas como segmento do nome. */
export const SIGLAS_DE_ESPECIALIDADE = [
  'ARQ', 'EST', 'MAC', 'HID', 'EEL', 'AVAC', 'MEC', 'ELE', 'AGU', 'ESG', 'GAS', 'SEG', 'TEL', 'PAV', 'TOP',
] as const

const SIGLAS = new Set<string>(SIGLAS_DE_ESPECIALIDADE)

/** Comprimento máximo da parte do ficheiro no nome curto. */
export const MAX_NOME_CURTO = 28

export interface ModeloComNome {
  id: string
  fileName: string
}

export interface NomeDoModelo {
  /** A sigla de especialidade encontrada no nome, ou null. */
  sigla: string | null
  /** O nome sem extensão, truncado ao meio. */
  base: string
  /** "MAC · Cha139-…-MAC-AP-0001", ou só a base sem sigla. */
  curto: string
  /** A forma mais curta: a sigla, ou a base. */
  minimo: string
  /** O nome que a lista de modelos mostra (para o tooltip). */
  completo: string
}

function semExtensao(nome: string): string {
  return nome.replace(/\.(ifc|ifczip|ifcxml|ifcx|frag)$/i, '')
}

/** A sigla de especialidade do nome do ficheiro, ou null. Procura do fim
 *  para o início: a convenção de nomes põe a especialidade depois do
 *  projecto e do autor (CHA139-VN2-XXX-XXX-MD3-EST-AP-0001). */
export function siglaDoFicheiro(nome: string): string | null {
  const segmentos = semExtensao(nome).split(/[-_]+/).filter(Boolean)
  for (let i = segmentos.length - 1; i >= 0; i--) {
    const s = segmentos[i].toUpperCase()
    if (SIGLAS.has(s)) return s
  }
  return null
}

/** Trunca ao meio com reticências, mantendo o início (o projecto) e,
 *  com mais espaço, o fim (a especialidade e o número do ficheiro). */
export function truncarAoMeio(texto: string, max: number): string {
  if (texto.length <= max) return texto
  if (max <= 1) return '…'
  const resto = max - 1
  const inicio = Math.floor(resto * 0.4)
  const fim = resto - inicio
  return `${texto.slice(0, inicio)}…${texto.slice(texto.length - fim)}`
}

/** O nome que a lista de modelos mostra: o ficheiro, ou o id quando há
 *  outro modelo do mesmo ficheiro (o id é então "Nome (2).ifc"). */
export function nomeNaLista(modelo: ModeloComNome, todos: readonly ModeloComNome[] = []): string {
  return todos.some((o) => o.id !== modelo.id && o.fileName === modelo.fileName) ? modelo.id : modelo.fileName
}

export function nomeDoModelo(
  modelo: ModeloComNome,
  todos: readonly ModeloComNome[] = [],
  max = MAX_NOME_CURTO,
): NomeDoModelo {
  const completo = nomeNaLista(modelo, todos)
  const sigla = siglaDoFicheiro(completo)
  const base = truncarAoMeio(semExtensao(completo), max)
  return {
    sigla,
    base,
    curto: sigla ? `${sigla} · ${base}` : base,
    minimo: sigla ?? base,
    completo,
  }
}
